// src/components/forms/TagInput.jsx
import React, { useRef } from 'react';
import { IoClose, IoAddOutline } from 'react-icons/io5';

const TagInput = ({
  label,
  tags,
  setTags,
  placeholder = "Add tags (press Enter or comma)",
  tagInputValue,
  setTagInputValue,
  error,
  required = false,
  maxTags = null,
  className = '',
  helpText = "Press Enter, comma, or tab to add a tag",
  icon
}) => {
  const tagInputRef = useRef(null);

  const handleTagInputChange = (e) => {
    setTagInputValue(e.target.value);
  };

  const addTag = () => {
    const trimmedInput = tagInputValue.trim();
    if (trimmedInput && !tags.includes(trimmedInput)) {
      // Check for max tags limit
      if (maxTags !== null && tags.length >= maxTags) {
        return;
      }
      const newTags = [...tags, trimmedInput];
      setTags(newTags);
      setTagInputValue('');
    }
  };

  const removeTag = (index) => {
    const newTags = [...tags];
    newTags.splice(index, 1);
    setTags(newTags);
  };

  const handleTagInputKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addTag();
    } else if (e.key === 'Backspace' && tagInputValue === '' && tags.length > 0) {
      // Remove the last tag when backspace is pressed on empty input
      removeTag(tags.length - 1);
    }
  };

  const handleTagInputBlur = () => {
    if (tagInputValue.trim()) {
      addTag();
    }
  };

  const focusTagInput = () => {
    tagInputRef.current?.focus();
  };

  const isMaxTagsReached = maxTags !== null && tags.length >= maxTags;

  return (
    <div className={['tags-input-container', className].filter(Boolean).join(' ')}>
      {label && (
        <label className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-900 dark:text-white">
          {icon && (
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-blue-50 text-blue-700 ring-1 ring-blue-100 dark:bg-blue-950/40 dark:text-blue-200 dark:ring-blue-900/40">
              {icon}
            </span>
          )}
          <span className="inline-flex items-center gap-1">
            {label}
            {required && <span className="text-red-500">*</span>}
          </span>
        </label>
      )}

      <div
        className={[
          'tags-input-wrapper',
          'flex',
          'min-h-[48px]',
          'w-full',
          'flex-wrap',
          'items-center',
          'gap-2',
          'rounded-xl',
          'border',
          'bg-white',
          'px-3',
          'py-2',
          'shadow-sm',
          'transition',
          'focus-within:ring-2',
          'dark:bg-slate-900',
          error
            ? 'border-red-300 ring-red-500/20 dark:border-red-900/50 dark:ring-red-500/20'
            : 'border-slate-200 focus-within:border-blue-300 focus-within:ring-blue-500/20 dark:border-slate-800 dark:focus-within:border-blue-900/50 dark:focus-within:ring-blue-500/20'
        ].join(' ')}
        onClick={focusTagInput}
        role="group"
        aria-label={label || 'Tags input'}
      >
        {tags.map((tag, index) => (
          <div
            className="tag-item inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/40 dark:text-blue-200"
            key={index}
          >
            <span className="max-w-[220px] truncate">{tag}</span>
            <button
              type="button"
              className="tag-close inline-flex h-5 w-5 items-center justify-center rounded-full text-blue-700/80 transition hover:bg-blue-100 hover:text-blue-900 dark:text-blue-200/80 dark:hover:bg-blue-900/30 dark:hover:text-blue-100"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(index);
              }}
              aria-label={`Remove tag ${tag}`}
            >
              <IoClose className="text-sm" />
            </button>
          </div>
        ))}

        <input
          ref={tagInputRef}
          type="text"
          value={tagInputValue}
          onChange={handleTagInputChange}
          onKeyDown={handleTagInputKeyDown}
          onBlur={handleTagInputBlur}
          className="tags-input min-w-[140px] flex-1 bg-transparent px-1 py-1 text-sm font-medium text-slate-900 outline-none placeholder:text-slate-400 disabled:cursor-not-allowed disabled:opacity-60 dark:text-white dark:placeholder:text-slate-500"
          placeholder={tags.length === 0 ? placeholder : ''}
          disabled={isMaxTagsReached}
        />

        {tagInputValue && !isMaxTagsReached && (
          <button
            type="button"
            className="add-tag-btn inline-flex h-9 w-9 items-center justify-center rounded-xl text-blue-700 transition hover:bg-blue-50 hover:text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500/30 dark:text-blue-200 dark:hover:bg-blue-900/30 dark:hover:text-blue-100"
            onClick={(e) => {
              e.stopPropagation();
              addTag();
            }}
            aria-label="Add tag"
          >
            <IoAddOutline className="text-lg" />
          </button>
        )}
      </div>

      {error ? (
        <div className="mt-2 text-sm font-medium text-red-600">{error}</div>
      ) : (
        <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          {isMaxTagsReached ? `Maximum ${maxTags} tags reached` : helpText}
        </div>
      )}
    </div>
  );
};

export default TagInput;
